import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ClinicalOnboardingService } from '../../services/clinical-onboarding.service';
import { ToastService } from '../../services/general/toast.service';
import {
  ClinicianDetailResponse,
  EmployerType,
  FiscalRegime,
  PaAuthorizationStatus,
} from '../../models/clinical-onboarding.model';

/**
 * DBEK-413 (feedback utente: "rendere modificabili da parte dell'amministratore
 * tutti i campi dati di un clinico"): finora il pannello di dettaglio era
 * interamente in sola lettura - l'unico modo di correggere anagrafica/dati
 * contrattuali era che lo facesse il clinico stesso dal wizard, e nemmeno quel
 * percorso copriva il compenso (gross_amount, deciso dal gestore: nessun
 * endpoint lo scriveva mai prima di questo ticket).
 *
 * Stessa struttura a due sezioni (Datore di lavoro, Dati fiscali) gia' in uso nel
 * wizard del clinico - IBAN/codice fiscale mostrati sempre vuoti con un hint
 * "lascia vuoto per non modificare" quando gia' presenti, mai il valore in
 * chiaro (stessa scelta gia' fatta per il clinico stesso).
 *
 * Ridisegno UI in tab (feedback utente: Dettagli/Dati contrattuali/Documenti/
 * Storico): nome/cognome/email si modificano ora direttamente nella tab
 * "Dettagli" del pannello di dettaglio - questa modale, aperta dalla tab "Dati
 * contrattuali", ne ha perso i campi visibili ma li mantiene comunque nel form
 * (name/last_name/email restano SEMPRE obbligatori lato server in ogni
 * chiamata) - il valore inviato e' semplicemente quello gia' presente,
 * invariato.
 */
@Component({
  selector: 'app-modal-edit-clinician-data',
  templateUrl: './modal-edit-clinician-data.component.html',
  styleUrls: ['./modal-edit-clinician-data.component.scss'],
  standalone: false,
})
export class ModalEditClinicianDataComponent {
  saving = false;
  form: FormGroup;
  readonly EmployerType = EmployerType;
  readonly FiscalRegime = FiscalRegime;
  readonly PaAuthorizationStatus = PaAuthorizationStatus;

  constructor(
    public dialogRef: MatDialogRef<ModalEditClinicianDataComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { surveyId: string | number; detail: ClinicianDetailResponse },
    private fb: FormBuilder,
    private clinicalOnboardingService: ClinicalOnboardingService,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {
    const { candidate, engagement } = data.detail;

    this.form = this.fb.group({
      name: [candidate.first_name, Validators.required],
      last_name: [candidate.last_name || ''],
      email: [candidate.email, [Validators.required, Validators.email]],
      title: [engagement?.title || ''],
      employer_type: [engagement?.employer_type || '', Validators.required],
      employer_name: [engagement?.employer_name || ''],
      iban: [''],
      tax_code: [''],
      fiscal_regime: [engagement?.fiscal_regime || ''],
      gross_amount: [engagement?.gross_amount ?? null],
      pa_authorization_status: [engagement?.pa_authorization_status || ''],
    });
  }

  get isEmployerPublic(): boolean {
    return this.form.get('employer_type').value === EmployerType.PUBLIC;
  }

  get hasIban(): boolean {
    return !!this.data.detail.engagement?.has_iban;
  }

  get hasTaxCode(): boolean {
    return !!this.data.detail.engagement?.has_tax_code;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    const value = this.form.value;

    this.clinicalOnboardingService.updateClinicianData(
      this.data.surveyId,
      this.data.detail.candidate.id,
      {
        name: value.name,
        last_name: value.last_name || null,
        email: value.email,
        title: value.title || null,
        employer_name: value.employer_name || null,
        employer_type: value.employer_type,
        iban: value.iban || null,
        tax_code: value.tax_code || null,
        fiscal_regime: value.fiscal_regime || null,
        gross_amount: value.gross_amount ?? null,
        pa_authorization_status: value.pa_authorization_status || null,
      },
      () => {
        this.saving = false;
        this.toastService.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('components.clinical-onboarding.clinician-detail.data-saved'),
        );
        this.dialogRef.close(true);
      },
      () => {
        this.saving = false;
      },
    );
  }

  closeDialogue(): void {
    this.dialogRef.close();
  }
}
